/**
 * HoursView.js — Restaurant hours management
 *
 * Three sections:
 *   Regular Hours  — weekly schedule (openTime/closeTime per day, or closed)
 *   Special Hours  — one-off date overrides (e.g. holiday hours, early close)
 *   Holidays       — fully closed dates
 *
 * Data model mirrors the POS app (AdjustHours.js):
 *   restaurants_data/{restaurantId}.hours.regularHours  array<{ dayOfWeek, openTime, closeTime, isClosed }>
 *   restaurants_data/{restaurantId}.hours.customHours   array<{ openTime: Timestamp, closeTime: Timestamp, reason }>
 *   restaurants_data/{restaurantId}.hours.holidays      array<{ date: Timestamp, reason }>
 *
 * Writes directly to Firestore then calls Cloud Functions for GBP sync.
 * Access controlled by permissions.manageHours (owner always, others if granted).
 *
 * Unsaved-changes guard:
 *   - Sub-tab navigation: guarded locally within this component
 *   - Main portal-tab navigation: dirty state reported up via onDirtyChange(isDirty, saveFn)
 */

import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import UnsavedChangesModal from '../../utilities/UnsavedChangesModal';

const functions = getFunctions();
const updateRegularHours = httpsCallable(functions, 'updateRegularHours');
const updateCustomHours  = httpsCallable(functions, 'updateCustomHours');
const updateHolidays     = httpsCallable(functions, 'updateHolidays');

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_REGULAR = DAYS.map((_, i) => ({
  dayOfWeek:  i,
  openTime:   '11:00',
  closeTime:  '22:00',
  isClosed:   false,
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function tsToDateStr(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-CA');
}

function tsToTimeStr(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toTimeString().slice(0, 5);
}

function buildTimestamp(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, mo, day] = dateStr.split('-').map(Number);
  const [h, m] = timeStr.split(':').map(Number);
  return Timestamp.fromDate(new Date(y, mo - 1, day, h, m, 0));
}

function buildDateTimestamp(dateStr) {
  if (!dateStr) return null;
  const [y, mo, day] = dateStr.split('-').map(Number);
  return Timestamp.fromDate(new Date(y, mo - 1, day, 0, 0, 0));
}

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const SectionHeader = ({ title, description }) => (
  <div className="mb-5">
    <h3 className="text-base font-semibold text-neutral-800">{title}</h3>
    {description && <p className="text-sm text-neutral-400 mt-0.5">{description}</p>}
  </div>
);

const SaveBar = ({ saving, saved, error, onSave, dirty }) => (
  <div className="flex items-center gap-3 mt-5">
    <button
      onClick={onSave}
      disabled={saving || !dirty}
      className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors"
    >
      {saving ? 'Saving…' : 'Save Changes'}
    </button>
    {saved && !saving && <span className="text-sm text-emerald-600 font-medium">Saved</span>}
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// ─── Regular Hours tab ────────────────────────────────────────────────────────

const RegularHoursTab = ({ restaurantId, initialHours, onDirtyChange }) => {
  const [hours, setHours]   = useState(
    (initialHours && initialHours.length === 7)
      ? initialHours.map(d => ({ ...d }))
      : DEFAULT_REGULAR.map(d => ({ ...d }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');
  const [dirty, setDirty]   = useState(false);
  const saveFnRef = useRef(null);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        'hours.regularHours': hours,
      });
      try { await updateRegularHours({ restaurantId }); } catch (_) { /* GBP sync non-fatal */ }
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
      throw err; // re-throw so the guard modal knows save failed
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;

  const update = (i, field, value) => {
    setHours(prev => prev.map((day, idx) => idx === i ? { ...day, [field]: value } : day));
    setSaved(false);
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current());
    }
  };

  return (
    <div>
      <SectionHeader
        title="Weekly Schedule"
        description="Set your regular open and close times for each day of the week."
      />
      <div className="space-y-2">
        {hours.map((day, i) => (
          <div
            key={i}
            className={`flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 rounded-xl border transition-colors ${
              day.isClosed ? 'bg-neutral-50 border-neutral-200' : 'bg-white border-neutral-200'
            }`}
          >
            {/* Day label — use index i, not day.dayOfWeek, to guarantee correct label */}
            <div className="w-24 shrink-0">
              <span className={`text-sm font-medium ${day.isClosed ? 'text-neutral-400' : 'text-neutral-800'}`}>
                {DAYS[i]}
              </span>
            </div>

            {/* Closed toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
              <div
                onClick={() => update(i, 'isClosed', !day.isClosed)}
                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                  day.isClosed ? 'bg-neutral-300' : 'bg-emerald-500'
                }`}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  day.isClosed ? 'translate-x-0' : 'translate-x-4'
                }`} />
              </div>
              <span className="text-xs text-neutral-500 w-10">{day.isClosed ? 'Closed' : 'Open'}</span>
            </label>

            {/* Time inputs */}
            {!day.isClosed && (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="time"
                  value={day.openTime}
                  onChange={e => update(i, 'openTime', e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white"
                />
                <span className="text-xs text-neutral-400 shrink-0">to</span>
                <input
                  type="time"
                  value={day.closeTime}
                  onChange={e => update(i, 'closeTime', e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 bg-white"
                />
              </div>
            )}
            {day.isClosed && (
              <span className="text-xs text-neutral-300 italic pl-2">Closed all day</span>
            )}
          </div>
        ))}
      </div>
      <SaveBar saving={saving} saved={saved} error={error} onSave={handleSave} dirty={dirty} />
    </div>
  );
};

// ─── Special Hours tab ────────────────────────────────────────────────────────

const SpecialHoursTab = ({ restaurantId, initialCustom, onDirtyChange }) => {
  const toRow = (c) => ({
    dateStr:  tsToDateStr(c.openTime),
    openStr:  tsToTimeStr(c.openTime),
    closeStr: tsToTimeStr(c.closeTime),
    reason:   c.reason || '',
  });

  const [rows, setRows]     = useState((initialCustom || []).map(toRow));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');
  const [dirty, setDirty]   = useState(false);
  const saveFnRef = useRef(null);

  const markDirty = () => {
    setSaved(false);
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current());
    }
  };

  const addRow = () => {
    setRows(prev => [...prev, { dateStr: '', openStr: '11:00', closeStr: '22:00', reason: '' }]);
    markDirty();
  };

  const updateRow = (i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
    markDirty();
  };

  const removeRow = (i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i));
    markDirty();
  };

  const handleSave = async () => {
    for (const r of rows) {
      if (!r.dateStr) { setError('Each entry needs a date.'); throw new Error('validation'); }
      if (!r.openStr || !r.closeStr) { setError('Each entry needs open and close times.'); throw new Error('validation'); }
    }
    setSaving(true);
    setError('');
    try {
      const customHours = rows.map(r => ({
        openTime:  buildTimestamp(r.dateStr, r.openStr),
        closeTime: buildTimestamp(r.dateStr, r.closeStr),
        reason:    r.reason.trim(),
      }));
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        'hours.customHours': customHours,
      });
      try { await updateCustomHours({ restaurantId }); } catch (_) { /* GBP sync non-fatal */ }
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
    } catch (err) {
      if (err.message !== 'validation') setError(err.message || 'Failed to save. Please try again.');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;

  return (
    <div>
      <SectionHeader
        title="Special Hours"
        description="Override hours for specific dates — early closes, extended hours, etc."
      />
      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="flex items-center justify-center h-24 border-2 border-dashed border-neutral-200 rounded-xl">
            <p className="text-sm text-neutral-400">No special hours set. Add one below.</p>
          </div>
        )}
        {rows.map((row, i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={row.dateStr}
                    onChange={e => updateRow(i, 'dateStr', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Opens</label>
                  <input
                    type="time"
                    value={row.openStr}
                    onChange={e => updateRow(i, 'openStr', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Closes</label>
                  <input
                    type="time"
                    value={row.closeStr}
                    onChange={e => updateRow(i, 'closeStr', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
              </div>
              <button
                onClick={() => removeRow(i)}
                className="mt-5 text-neutral-300 hover:text-red-500 transition-colors text-lg leading-none shrink-0"
              >
                ×
              </button>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Reason (optional)</label>
              <input
                type="text"
                value={row.reason}
                onChange={e => updateRow(i, 'reason', e.target.value)}
                placeholder="e.g. Mother's Day extended hours"
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={addRow}
        className="mt-3 text-sm text-neutral-500 hover:text-neutral-800 font-medium transition-colors"
      >
        + Add Special Hours
      </button>
      <SaveBar saving={saving} saved={saved} error={error} onSave={handleSave} dirty={dirty} />
    </div>
  );
};

// ─── Holidays tab ─────────────────────────────────────────────────────────────

const HolidaysTab = ({ restaurantId, initialHolidays, onDirtyChange }) => {
  const toRow = (h) => ({
    dateStr: tsToDateStr(h.date),
    reason:  h.reason || '',
  });

  const [rows, setRows]     = useState((initialHolidays || []).map(toRow));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState('');
  const [dirty, setDirty]   = useState(false);
  const saveFnRef = useRef(null);

  const markDirty = () => {
    setSaved(false);
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true, () => saveFnRef.current());
    }
  };

  const addRow = () => {
    setRows(prev => [...prev, { dateStr: '', reason: '' }]);
    markDirty();
  };

  const updateRow = (i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
    markDirty();
  };

  const removeRow = (i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i));
    markDirty();
  };

  const handleSave = async () => {
    for (const r of rows) {
      if (!r.dateStr) { setError('Each holiday needs a date.'); throw new Error('validation'); }
    }
    setSaving(true);
    setError('');
    try {
      const holidays = rows.map(r => ({
        date:   buildDateTimestamp(r.dateStr),
        reason: r.reason.trim(),
      }));
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        'hours.holidays': holidays,
      });
      try { await updateHolidays({ restaurantId }); } catch (_) { /* GBP sync non-fatal */ }
      setSaved(true);
      setDirty(false);
      onDirtyChange?.(false, null);
    } catch (err) {
      if (err.message !== 'validation') setError(err.message || 'Failed to save. Please try again.');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  saveFnRef.current = handleSave;

  const sortedRows = [...rows].map((r, i) => ({ ...r, _i: i })).sort((a, b) =>
    a.dateStr.localeCompare(b.dateStr)
  );

  return (
    <div>
      <SectionHeader
        title="Holidays &amp; Closures"
        description="Mark dates when you are fully closed. These are shown to customers and sync to Google Business."
      />
      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="flex items-center justify-center h-24 border-2 border-dashed border-neutral-200 rounded-xl">
            <p className="text-sm text-neutral-400">No holidays set. Add one below.</p>
          </div>
        )}
        {sortedRows.map((row) => {
          const i = row._i;
          return (
            <div key={i} className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3">
              <input
                type="date"
                value={row.dateStr}
                onChange={e => updateRow(i, 'dateStr', e.target.value)}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 shrink-0"
              />
              <input
                type="text"
                value={row.reason}
                onChange={e => updateRow(i, 'reason', e.target.value)}
                placeholder="e.g. Thanksgiving Day"
                className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
              <button
                onClick={() => removeRow(i)}
                className="text-neutral-300 hover:text-red-500 transition-colors text-lg leading-none shrink-0"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <button
        onClick={addRow}
        className="mt-3 text-sm text-neutral-500 hover:text-neutral-800 font-medium transition-colors"
      >
        + Add Holiday
      </button>
      <SaveBar saving={saving} saved={saved} error={error} onSave={handleSave} dirty={dirty} />
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'regular',  label: 'Regular Hours'  },
  { id: 'special',  label: 'Special Hours'  },
  { id: 'holidays', label: 'Holidays'       },
];

const HoursView = ({ restaurantId, onDirtyChange }) => {
  const [loading, setLoading]           = useState(true);
  const [regularHours, setRegularHours] = useState(null);
  const [customHours, setCustomHours]   = useState(null);
  const [holidays, setHolidays]         = useState(null);
  const [activeTab, setActiveTab]       = useState('regular');
  const [fetchError, setFetchError]     = useState('');

  // Sub-tab unsaved changes guard
  const [pendingSubTab, setPendingSubTab] = useState(null); // shows guard modal when set
  const [guardSaving, setGuardSaving]     = useState(false);
  const subSaveFnRef = useRef(null);
  const subDirtyRef  = useRef(false);

  // Called by active sub-tab component when its dirty state changes
  const handleSubDirtyChange = (isDirty, saveFn) => {
    subDirtyRef.current  = isDirty;
    subSaveFnRef.current = isDirty ? saveFn : null;
    onDirtyChange?.(isDirty, isDirty ? saveFn : null);
  };

  // Intercept sub-tab clicks
  const handleSubTabClick = (tabId) => {
    if (tabId === activeTab) return;
    if (subDirtyRef.current) {
      setPendingSubTab(tabId);
    } else {
      clearSubDirty();
      setActiveTab(tabId);
    }
  };

  const clearSubDirty = () => {
    subDirtyRef.current  = false;
    subSaveFnRef.current = null;
    onDirtyChange?.(false, null);
  };

  const handleSubGuardSave = async () => {
    setGuardSaving(true);
    try {
      await subSaveFnRef.current?.();
      clearSubDirty();
      setActiveTab(pendingSubTab);
      setPendingSubTab(null);
    } catch (_) {
      // Save failed — keep guard open
    } finally {
      setGuardSaving(false);
    }
  };

  const handleSubGuardLeave = () => {
    clearSubDirty();
    setActiveTab(pendingSubTab);
    setPendingSubTab(null);
  };

  const handleSubGuardStay = () => {
    setPendingSubTab(null);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (snap.exists()) {
          const data = snap.data();
          setRegularHours(data?.hours?.regularHours || null);
          setCustomHours(data?.hours?.customHours  || []);
          setHolidays(data?.hours?.holidays         || []);
        }
      } catch (err) {
        setFetchError('Could not load hours. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  if (loading) {
    return <p className="text-sm text-neutral-400 text-center py-12">Loading hours…</p>;
  }

  if (fetchError) {
    return <p className="text-sm text-red-500 text-center py-12">{fetchError}</p>;
  }

  return (
    <div className="max-w-2xl space-y-5">
      {/* Tab strip */}
      <div className="flex gap-2 border-b border-neutral-200 pb-0 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleSubTabClick(tab.id)}
            className={`shrink-0 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-400 hover:text-neutral-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div>
        {activeTab === 'regular' && (
          <RegularHoursTab
            restaurantId={restaurantId}
            initialHours={regularHours}
            onDirtyChange={handleSubDirtyChange}
          />
        )}
        {activeTab === 'special' && (
          <SpecialHoursTab
            restaurantId={restaurantId}
            initialCustom={customHours}
            onDirtyChange={handleSubDirtyChange}
          />
        )}
        {activeTab === 'holidays' && (
          <HolidaysTab
            restaurantId={restaurantId}
            initialHolidays={holidays}
            onDirtyChange={handleSubDirtyChange}
          />
        )}
      </div>

      {/* Sub-tab unsaved changes guard */}
      {pendingSubTab !== null && (
        <UnsavedChangesModal
          saving={guardSaving}
          onSave={handleSubGuardSave}
          onLeave={handleSubGuardLeave}
          onStay={handleSubGuardStay}
        />
      )}
    </div>
  );
};

export default HoursView;
